import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  FarmSummary,
  createFarm,
  deleteFarm,
  listFarms,
  updateFarm,
} from "../../services/productService";

type SourceFormState = {
  name: string;
  location: string;
  description: string;
  contact_person: string;
  contact_phone: string;
  certification_type: string;
  certification_number: string;
  size_hectares: string;
  latitude: string;
  longitude: string;
  farm_images_csv: string;
  is_active: boolean;
};

const EMPTY_FORM: SourceFormState = {
  name: "",
  location: "",
  description: "",
  contact_person: "",
  contact_phone: "",
  certification_type: "",
  certification_number: "",
  size_hectares: "",
  latitude: "",
  longitude: "",
  farm_images_csv: "",
  is_active: true,
};

const parseJson = <T,>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const getTenantId = () => {
  const tenant = parseJson<{ id?: string }>(localStorage.getItem("currentTenant"));
  const userData = parseJson<{ tenant_id?: string }>(localStorage.getItem("user_data"));
  const user = parseJson<{ tenant_id?: string }>(localStorage.getItem("user"));
  return tenant?.id || userData?.tenant_id || user?.tenant_id || "";
};

const AdminSources: React.FC = () => {
  const { hasCapability } = useAuth();
  const canAccess = hasCapability("capability:admin_console");
  const tenantId = useMemo(() => getTenantId(), []);

  const [sources, setSources] = useState<FarmSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingSource, setEditingSource] = useState<FarmSummary | null>(null);
  const [form, setForm] = useState<SourceFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadSources = async (isRefresh = false) => {
    if (!canAccess) return;
    if (!tenantId) {
      setError("Tenant context not found. Re-login to reload tenant session.");
      setLoading(false);
      return;
    }
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      setError(null);
      const data = await listFarms(tenantId);
      setSources(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load sources");
    } finally {
      if (isRefresh) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccess) return;
    loadSources();
  }, [canAccess, tenantId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sources;
    return sources.filter((s) =>
      [s.name, s.location, s.description, s.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [sources, search]);

  const openCreate = () => {
    setEditingSource(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (source: FarmSummary) => {
    setEditingSource(source);
    setForm({
      name: source.name || "",
      location: source.location || "",
      description: source.description || "",
      contact_person: source.contact_person || "",
      contact_phone: source.contact_phone || "",
      certification_type: source.certification_type || "",
      certification_number: source.certification_number || "",
      size_hectares: source.size_hectares != null ? String(source.size_hectares) : "",
      latitude:
        source.coordinates && typeof source.coordinates === "object" && "lat" in source.coordinates
          ? String((source.coordinates as any).lat ?? "")
          : "",
      longitude:
        source.coordinates && typeof source.coordinates === "object" && "lng" in source.coordinates
          ? String((source.coordinates as any).lng ?? "")
          : "",
      farm_images_csv: Array.isArray(source.farm_images) ? source.farm_images.join(", ") : "",
      is_active: source.is_active !== false,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingSource(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId) return toast.error("Tenant context missing");
    if (!form.name.trim()) return toast.error("Source name is required");
    const sizeVal = form.size_hectares.trim() ? Number(form.size_hectares) : undefined;
    if (sizeVal !== undefined && Number.isNaN(sizeVal)) return toast.error("Size (hectares) must be a valid number");
    const latVal = form.latitude.trim() ? Number(form.latitude) : undefined;
    const lngVal = form.longitude.trim() ? Number(form.longitude) : undefined;
    if (latVal !== undefined && Number.isNaN(latVal)) return toast.error("Latitude must be a valid number");
    if (lngVal !== undefined && Number.isNaN(lngVal)) return toast.error("Longitude must be a valid number");
    const coordinates =
      latVal !== undefined || lngVal !== undefined
        ? {
            ...(latVal !== undefined ? { lat: latVal } : {}),
            ...(lngVal !== undefined ? { lng: lngVal } : {}),
          }
        : undefined;
    const farmImages = form.farm_images_csv
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    const payload = {
      name: form.name.trim(),
      location: form.location.trim() || undefined,
      description: form.description.trim() || undefined,
      contact_person: form.contact_person.trim() || undefined,
      contact_phone: form.contact_phone.trim() || undefined,
      certification_type: form.certification_type.trim() || undefined,
      certification_number: form.certification_number.trim() || undefined,
      size_hectares: sizeVal,
      coordinates: coordinates && Object.keys(coordinates).length > 0 ? coordinates : undefined,
      farm_images: farmImages.length > 0 ? farmImages : undefined,
      is_active: form.is_active,
    };
    setSubmitting(true);
    try {
      if (editingSource) {
        const updated = await updateFarm(tenantId, editingSource.id, payload);
        setSources((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        toast.success("Source updated");
      } else {
        const created = await createFarm(tenantId, payload);
        setSources((prev) => [created, ...prev]);
        toast.success("Source created");
      }
      closeForm();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to save source");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (source: FarmSummary) => {
    if (!tenantId) return;
    if (!window.confirm(`Delete source "${source.name}"?`)) return;
    setBusyId(source.id);
    try {
      await deleteFarm(tenantId, source.id);
      setSources((prev) => prev.filter((s) => s.id !== source.id));
      toast.success("Source deleted");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to delete source");
    } finally {
      setBusyId(null);
    }
  };

  if (!canAccess) {
    return <div className="p-8 text-center text-red-600 font-semibold">Access denied. Admins only.</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Source Registry</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage product sources (farms, open markets, suppliers) used by products.
            {tenantId ? <span className="ml-2 font-mono text-xs">Tenant: {tenantId}</span> : null}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => loadSources(true)}
            disabled={refreshing}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
          <button
            onClick={() => {
              openCreate();
              setForm((prev) => ({ ...prev, name: "Open Market" }));
            }}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            Open Market Source
          </button>
          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-lg bg-green-700 text-white font-semibold hover:bg-green-800"
          >
            Add Source
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Total Sources</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{sources.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Open Market Sources</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">
            {sources.filter((s) => (s.name || "").toLowerCase().includes("open market")).length}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Linked Usage</div>
          <div className="text-sm text-gray-600 mt-2">
            Use these sources in <Link className="text-green-700 font-medium" to="/admin/products">Product Management</Link>.
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search source name, location, description, ID..."
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      {loading && <div className="bg-white rounded-xl border border-gray-200 p-6 text-gray-600">Loading sources...</div>}

      {!loading && error && (
        <div className="bg-white rounded-xl border border-red-200 p-6">
          <div className="text-red-700 font-semibold">{error}</div>
          {String(error).toLowerCase().includes("authentication") && (
            <div className="text-sm text-gray-600 mt-2">Re-login to refresh this app session.</div>
          )}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="text-lg font-semibold text-gray-900 mb-2">No sources found</div>
          <div className="text-sm text-gray-600 mb-4">
            Create a farm source or an open-market source before adding products.
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                openCreate();
                setForm((prev) => ({ ...prev, name: "Open Market" }));
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              Create Open Market Source
            </button>
            <button
              onClick={openCreate}
              className="px-4 py-2 rounded-lg bg-green-700 text-white font-semibold hover:bg-green-800"
            >
              Create Source
            </button>
          </div>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Source</th>
                  <th className="px-4 py-3 text-left font-semibold">Location</th>
                  <th className="px-4 py-3 text-left font-semibold">Contact</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-left font-semibold">Description</th>
                  <th className="px-4 py-3 text-left font-semibold">ID</th>
                  <th className="px-4 py-3 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((source) => (
                  <tr key={source.id} className="border-t border-gray-100">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{source.name}</div>
                      {(source.name || "").toLowerCase().includes("open market") && (
                        <div className="text-xs text-amber-700 mt-1">Open market source</div>
                      )}
                      {source.certification_type && (
                        <div className="text-xs text-gray-500 mt-1">
                          Cert: {source.certification_type}
                          {source.certification_number ? ` (${source.certification_number})` : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{source.location || "-"}</td>
                    <td className="px-4 py-3 text-gray-700">
                      <div>{source.contact_person || "-"}</div>
                      {source.contact_phone && <div className="text-xs text-gray-500 mt-1">{source.contact_phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          source.is_active === false ? "bg-gray-100 text-gray-700" : "bg-green-100 text-green-700"
                        }`}
                      >
                        {source.is_active === false ? "Inactive" : "Active"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-md">
                      <div className="truncate" title={source.description || ""}>{source.description || "-"}</div>
                      {(source.size_hectares != null || source.coordinates) && (
                        <div className="text-xs text-gray-500 mt-1">
                          {source.size_hectares != null ? `${source.size_hectares}ha` : ""}
                          {source.size_hectares != null && source.coordinates ? " • " : ""}
                          {source.coordinates && typeof source.coordinates === "object"
                            ? `(${(source.coordinates as any).lat ?? "-"}, ${(source.coordinates as any).lng ?? "-"})`
                            : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{source.id}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => openEdit(source)}
                          disabled={busyId === source.id}
                          className="px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(source)}
                          disabled={busyId === source.id}
                          className="px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-gray-200">
            <form onSubmit={handleSave}>
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{editingSource ? "Edit Source" : "Add Source"}</h2>
                  <p className="text-sm text-gray-600">
                    Use this registry for farms, open markets, and supplier sources used by products.
                  </p>
                </div>
                <button type="button" onClick={closeForm} className="text-gray-500 hover:text-gray-700">
                  Close
                </button>
              </div>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source Name</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Open Market - Lagos"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    value={form.location}
                    onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
                    placeholder="Mile 12 Market, Lagos"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    placeholder="Farm, supplier, or market source notes"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                    <input
                      value={form.contact_person}
                      onChange={(e) => setForm((prev) => ({ ...prev, contact_person: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                    <input
                      value={form.contact_phone}
                      onChange={(e) => setForm((prev) => ({ ...prev, contact_phone: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Certification Type</label>
                    <input
                      value={form.certification_type}
                      onChange={(e) => setForm((prev) => ({ ...prev, certification_type: e.target.value }))}
                      placeholder="organic / conventional"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Certification Number</label>
                    <input
                      value={form.certification_number}
                      onChange={(e) => setForm((prev) => ({ ...prev, certification_number: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Size (Hectares)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.size_hectares}
                      onChange={(e) => setForm((prev) => ({ ...prev, size_hectares: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      value={form.latitude}
                      onChange={(e) => setForm((prev) => ({ ...prev, latitude: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      value={form.longitude}
                      onChange={(e) => setForm((prev) => ({ ...prev, longitude: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image URLs (comma-separated)</label>
                  <textarea
                    value={form.farm_images_csv}
                    onChange={(e) => setForm((prev) => ({ ...prev, farm_images_csv: e.target.value }))}
                    rows={2}
                    placeholder="https://.../img1.jpg, https://.../img2.jpg"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                  />
                  Source is active
                </label>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg bg-green-700 text-white font-semibold hover:bg-green-800 disabled:opacity-50"
                >
                  {submitting ? "Saving..." : editingSource ? "Save Changes" : "Create Source"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSources;
